/*!
 * christina-apps.js — Christina Design System
 * KONTROLER ŁADOWANIA APLIKACJI. Runda 6, fala 6 (2026-08-04), wersja r6w6-1.5.
 *
 * JEDNO ŹRÓDŁO PRAWDY o tym, co i kiedy ładuje się na storefroncie.
 * Wcześniej ta logika była rozsypana po `layout/theme.liquid` (cztery osobne
 * bloki inline) i `snippets/christina-ai-advisor.liquid`. Rozsypana logika
 * dawała sprzeczne reguły: Shopify Forms było „odraczane" o 650 ms z twardym
 * limitem 8 s, więc i tak pobierało 206 KiB na stronie, na której nie ma
 * ani jednego <shopify-form>.
 *
 * TEN PLIK MUSI BYĆ ŁADOWANY SYNCHRONICZNIE W <head>, przed
 * {{ content_for_header }} i przed sekcją christina-marketing-pixels.
 * Powód: bramki działają przez podmianę appendChild/insertBefore na
 * prototypach <head>/<body> oraz przez flagę jdgm._loaderExecuted — jedno
 * i drugie musi istnieć ZANIM wykonają się skrypty aplikacji.
 * `defer` NIE wystarczy dla Klaviyo (jego bootstrap jest `async`).
 *
 * Zakres podmiany prototypów jest wąski: tylko HTMLHeadElement i
 * HTMLBodyElement (nie Node.prototype), tylko dla <script src> i <link href>
 * pasujących do listy bramek, i tylko do momentu otwarcia wszystkich bramek —
 * potem oryginalne deskryptory są przywracane.
 *
 * IDEMPOTENCJA I POWTÓRNE WEJŚCIA (sekcja H):
 *   · `if (window.__cbApps) return;` — drugie dołączenie pliku nic nie robi;
 *   · `pageshow` z `persisted` (powrót z bfcache) — sterta JS jest odtwarzana
 *     w całości, więc obserwatory żyją; wołamy mimo to `rescan()`, bo strona
 *     mogła w międzyczasie zmienić DOM (np. wróciliśmy na PDP z sekcją opinii);
 *   · `shopify:section:load` (edytor motywu, Section Rendering API) — sekcja
 *     może wnieść <shopify-form> albo widget opinii, których nie było przy
 *     starcie; `rescan()` uzbraja dla nich bramki.
 * Wszystkie funkcje `wire*` są napisane tak, żeby powtórne wywołanie było
 * bezpieczne (strażnicy `gate.open`, `JDGM.armed`, klasa `prof-gate-upgraded`).
 *
 * ==========================================================================
 * R6W6 — DLACZEGO TEN PLIK MIAŁ 18 DŁUGICH ZADAŃ (pomiar, nie przeczucie)
 * ==========================================================================
 * Ślad CDP (`traces\PRZED6-fn.json`) pokazał, że SAM kod tego pliku kosztuje
 * na PDP mobile ok. 110 ms scriptingu — a Lighthouse przypisywał mu zadania
 * po 140–187 ms. Rozbieżność ma jedną przyczynę: `long-tasks` przypisuje
 * CAŁE zadanie do skryptu, który je ROZPOCZĄŁ. Ten plik uruchamiał trzy
 * generatory zadań o wysokiej częstotliwości:
 *
 *   1. `pollPrivacy` — `setTimeout(…, 40)` przez maks. 10 s = do 250 osobnych
 *      zadań. Każde z nich mogło stać się początkiem zadania, w którym
 *      przeglądarka wykonała potem pracę aplikacji (theme.js, trekkie, Klaviyo).
 *      Teraz: rosnący odstęp 40 → 80 → 160 → … → 800 ms, maks. 20 prób.
 *      Realnie API zgody jest gotowe po 1–3 próbach (loadFeatures pojawia się
 *      razem z content_for_header, czyli tuż za tym plikiem).
 *
 *   2. Strażnik fokusu bannera — `pierwszePokazanie` odpytywał 40 razy co
 *      150 ms, a każda próba wołała `getBoundingClientRect()` +
 *      `getComputedStyle()`, czyli WYMUSZAŁA przeliczenie układu. Pomiar:
 *      17,6 ms na PDP i 21,3 ms na kolekcji w samej funkcji `widoczny`.
 *      Teraz: głównym detektorem jest IntersectionObserver na bannerze
 *      (zero odczytów układu), wspierany przez istniejący MutationObserver
 *      na atrybutach; odpytywanie zredukowane do 4 kontrolnych sprawdzeń.
 *
 *   3. `wireProfGate` — MutationObserver na #MainContent planował pracę przez
 *      `requestAnimationFrame`. rAF wykonuje się W ZADANIU KLATKI, razem ze
 *      stylem, układem i malowaniem, więc doklejał się do najdroższych zadań
 *      w całym przebiegu. Teraz `requestIdleCallback`, a okno obserwacji
 *      skrócone z 10 s do 6 s od `load`.
 *
 * Dodatkowo:
 *   · `gateFor` jest na gorącej ścieżce KAŻDEGO appendChild do <head>/<body>
 *     (46–66 ms w śladzie). Zamiast `for…in` po obiekcie GATES i podwójnego
 *     `getAttribute` — jeden test `tagName` na wejściu i dwa jawne warunki.
 *   · Zapasowy MutationObserver formularzy siedział na `document.body` z
 *     `subtree: true` DO KOŃCA SESJI i przy każdej mutacji robił
 *     `querySelectorAll('shopify-form')` po całym dokumencie. Teraz ma
 *     wstępny filtr (tylko dodane elementy) i rozbraja się 12 s po `load`.
 *   · `patchGravity` reaguje wyłącznie na dodanie węzła <style>.
 *
 * ZASADA, KTÓRA STĄD WYNIKA I OBOWIĄZUJE DALEJ:
 * ŻADNYCH PĘTLI ODPYTUJĄCYCH UKŁAD STRONY I ŻADNEJ PRACY OKRESOWEJ
 * W `requestAnimationFrame`. Wykrywanie ma być zdarzeniowe
 * (MutationObserver / IntersectionObserver / zdarzenia zgody), a praca
 * odroczona — w `requestIdleCallback`.
 * Historyczne ostrzeżenie: wersja `r6w2-1.3` miała strażnika fokusu bannera
 * odpytującego co 200 ms przez 120 s — na stronie kolekcji podniosło to TBT
 * z ~600 ms do 4 560 ms.
 *
 * Diagnostyka na produkcji: window.__cbApps.state
 */
(function () {
  'use strict';

  if (window.__cbApps) return;

  var doc = document;
  var CFG = window.__cbAppsCfg || {};
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);

  var api = (window.__cbApps = {
    version: 'r7a3-1.8',
    state: {},
    open: function (name) { openGate(name); },
    rescan: function () { rescan('reczny'); }
  });

  function now() { return Math.round((window.performance && performance.now()) || 0); }
  function mark(k, v) { api.state[k] = v + '@' + now() + 'ms'; }

  /* Praca odroczona. `requestIdleCallback` NIE wydłuża zadania klatki ani
     zadania, które go zaplanowało — w przeciwieństwie do rAF. */
  function idle(fn, timeout) {
    var r = window.requestIdleCallback;
    if (r) { try { r(fn, { timeout: timeout || 3000 }); return; } catch (e) { /* noop */ } }
    setTimeout(fn, 200);
  }

  function afterLoadIdle(fn) {
    if (doc.readyState === 'complete') idle(fn);
    else window.addEventListener('load', function () { idle(fn); }, { once: true });
  }

  /* ==========================================================================
   * A. BRAMKI ZASOBÓW — wstrzymywanie <script>/<link> aplikacji
   * ========================================================================== */

  var GATES = {
    /* Shopify Forms: 206 KiB, wg Lighthouse 194 KiB nieużywane.
       Wszystkie instancje formularzy tego sklepu mają layout "inline",
       czyli ZAWSZE istnieją w DOM jako <shopify-form>. Brak <shopify-form>
       = brak formularza na tej stronie = nie pobieramy nic.
       Siatką bezpieczeństwa dla ewentualnego layoutu „popup" (wstrzykiwanego
       przez shopify-forms-loader.js, którego NIE blokujemy) jest
       MutationObserver w wireForms(). */
    forms: {
      test: function (u) { return u.indexOf('forms-2485/assets/index.js') > -1; },
      held: [], open: false, rozstrzygnieta: false
    },
    /* Klaviyo: bootstrap klaviyo.js jest wstawiany przez parser (nie da się go
       przechwycić), ale CAŁA reszta — atrybucja, popupy, formularze onsite —
       jest doklejana przez document.head.appendChild w klaviyo.js, więc łapie
       się tutaj. Bramka otwiera się dopiero po zgodzie. */
    klaviyo: {
      test: function (u) { return u.indexOf('klaviyo.com') > -1; },
      held: [], open: false, rozstrzygnieta: false
    }
  };

  var PROTOS = [];
  try { PROTOS.push(HTMLHeadElement.prototype, HTMLBodyElement.prototype); } catch (e) { /* noop */ }
  var savedDescriptors = [];

  /* GORĄCA ŚCIEŻKA. Wywoływana dla każdego appendChild/insertBefore na
     <head> i <body> — także przez kod aplikacji. Musi kosztować tyle, co
     jedno porównanie łańcucha, dopóki węzeł nie jest skryptem ani arkuszem. */
  function gateFor(n) {
    if (!n || n.nodeType !== 1) return null;
    var tag = n.tagName;
    var u;
    if (tag === 'SCRIPT') u = n.getAttribute('src');
    else if (tag === 'LINK') u = n.getAttribute('href');
    else return null;
    if (!u) return null;
    var f = GATES.forms;
    if (!f.open && u.indexOf('forms-2485/assets/index.js') > -1) return f;
    var k = GATES.klaviyo;
    if (!k.open && u.indexOf('klaviyo.com') > -1) return k;
    return null;
  }

  function installPatch() {
    if (!PROTOS.length) return;
    savedDescriptors = PROTOS.map(function (p) {
      return {
        p: p,
        a: Object.getOwnPropertyDescriptor(p, 'appendChild'),
        i: Object.getOwnPropertyDescriptor(p, 'insertBefore')
      };
    });
    PROTOS.forEach(function (p) {
      p.appendChild = function (n) {
        var g = gateFor(n);
        if (g) { g.held.push({ n: n, p: this }); return n; }
        return Node.prototype.appendChild.apply(this, arguments);
      };
      p.insertBefore = function (n) {
        var g = gateFor(n);
        if (g) { g.held.push({ n: n, p: this }); return n; }
        return Node.prototype.insertBefore.apply(this, arguments);
      };
    });
  }

  function uninstallPatch() {
    savedDescriptors.forEach(function (o) {
      if (o.a) Object.defineProperty(o.p, 'appendChild', o.a); else { try { delete o.p.appendChild; } catch (e) {} }
      if (o.i) Object.defineProperty(o.p, 'insertBefore', o.i); else { try { delete o.p.insertBefore; } catch (e) {} }
    });
    savedDescriptors = [];
  }

  /* R7-A3 — KIEDY ZDEJMUJEMY PODMIENIONE appendChild/insertBefore.
     Podmiana prototypow lezy na drodze KAZDEJ wstawki <script>/<link> na
     stronie, takze cudzej, wiec kazde dlugie zadanie, w ktorym cokolwiek
     cos wstawia, ma nas na stosie wywolan i dostaje nasza etykiete.
     Dlatego bramka ma teraz dwa stany:
       · OTWARTA        — zasob jest juz dozwolony, wstrzymane wezly ida dalej;
       · ROZSTRZYGNIETA — wiadomo, ze przechwytywanie przestalo byc potrzebne.
     Rozstrzygniecie bez otwarcia zachodzi, gdy zapada decyzja, ze zasobu
     NIE WSTAWIMY: odrzucona zgoda (Klaviyo) albo brak <shopify-form> na
     stronie po wygasnieciu czujki (Shopify Forms). Gdy wszystkie bramki sa
     rozstrzygniete, oryginalne deskryptory wracaja na prototypy. */
  var patchZdjety = false;

  function allGatesResolved() {
    for (var k in GATES) if (GATES.hasOwnProperty(k) && !GATES[k].rozstrzygnieta) return false;
    return true;
  }

  function mozeZdjacPatch() {
    if (patchZdjety || !allGatesResolved()) return;
    patchZdjety = true;
    uninstallPatch();
    mark('interceptor', 'zdjety');
  }

  /* Bramka nie zostanie juz uzyta — nie zwalniamy wstrzymanych wezlow. */
  function resolveGate(name) {
    var g = GATES[name];
    if (!g || g.rozstrzygnieta) return;
    g.rozstrzygnieta = true;
    g.held.length = 0;
    mark(name, 'rozstrzygnieta-bez-otwarcia');
    mozeZdjacPatch();
  }

  function openGate(name) {
    var g = GATES[name];
    if (!g || g.open) return;
    g.open = true;
    g.rozstrzygnieta = true;
    mark(name, 'released');
    var queued = g.held.splice(0);
    mozeZdjacPatch();
    queued.forEach(function (h) {
      try {
        var src = h.n;
        var el = doc.createElement(src.tagName);
        for (var i = 0; i < src.attributes.length; i++) {
          el.setAttribute(src.attributes[i].name, src.attributes[i].value);
        }
        var parent = (h.p && h.p.nodeType === 1 && h.p.isConnected)
          ? h.p
          : (src.tagName === 'LINK' ? doc.head : doc.body);
        parent.appendChild(el);
      } catch (e) { /* noop */ }
    });
  }

  installPatch();

  /* ==========================================================================
   * B. JUDGE.ME — start dopiero przy sekcji opinii
   * --------------------------------------------------------------------------
   * NIE przechwytujemy metod aplikacji (jdgm.loadJS/loadCSS) — poprzednia próba
   * z R5E kończyła się TypeError, bo loader.js nadpisuje je własnymi
   * implementacjami zaraz po starcie.
   *
   * App embed `judgeme_core` nie ma żadnego oficjalnego trybu lazy/deferred:
   * jego `settings` są puste (config/settings_data.json), a w loader.js nie ma
   * ani jednej gałęzi sterowanej ustawieniem opóźnienia
   * (widget_advanced_speed_features steruje tylko wyborem metody ładowania,
   * nie momentem).
   *
   * Zostaje jedyna nieinwazyjna dźwignia — WŁASNY kontrakt loadera. Jego
   * pierwsza instrukcja to:
   *     if (window.jdgm = window.jdgm || {}, jdgm._loaderExecuted) return;
   * Ustawiamy tę flagę zanim loader.js (defer, w <body>) się wykona, więc
   * loader staje się no-opem: 0 pobrań widgetu. Gdy sekcja opinii zbliża się
   * do ekranu, zdejmujemy flagę i wstrzykujemy loader.js ponownie — wykonuje
   * się wtedy w całości, dokładnie tak jak zaprojektowała to aplikacja.
   *
   * App embed ZOSTAJE WŁĄCZONY w config/settings_data.json. Wyłączenie go
   * usunęłoby inline `jdgmSettings` (33 KB konfiguracji), `jdgm-settings-style`
   * i `jdgm-miracle-styles`, bez których widget nie ma się jak wyrenderować —
   * a wymagane jest zachowanie opinii, histogramu, sortowania, paginacji
   * i formularza dodania opinii.
   *
   * Gwiazdki i liczba opinii nad zgięciem są serwerowe (SVG z metapól) i ten
   * mechanizm ich nie dotyka.
   * ========================================================================== */

  var JDGM = { gated: false, armed: false, loaderSrc: '' };

  try {
    var _j = (window.jdgm = window.jdgm || {});
    if (!_j._loaderExecuted) { _j._loaderExecuted = true; JDGM.gated = true; }
  } catch (e) { /* noop */ }

  var JDGM_SEL = [
    '#judgeme_product_reviews',
    '.jdgm-review-widget',
    '.jdgm-all-reviews-page',
    '.jdgm-all-reviews-widget',
    '.jdgm-carousel',
    '.jdgm-revs-tab',
    '.jdgm-preview-badge',
    '.judgeme-preview-badge',
    '.jdgm-ugc-media',
    '.jdgm-medals',
    '.jdgm-videos-carousel',
    '.jdgm-cards-carousel',
    '.jdgm-popup-widget',
    '.jdgm-review-snippet-widget',
    '[data-widget="review"]',
    '[data-widget="all-reviews-v2025"]'
  ].join(', ');

  function judgemeStart() {
    if (!JDGM.gated) return;
    JDGM.gated = false;
    try {
      window.jdgm._loaderExecuted = false;
      var s = doc.createElement('script');
      s.src = JDGM.loaderSrc;
      s.async = true;
      s.className = 'jdgm-loader-deferred';
      doc.body.appendChild(s);
      mark('judgeme', 'started');
    } catch (e) { /* noop */ }
  }

  function wireJudgeMe() {
    if (!JDGM.gated) return;
    var tag = doc.querySelector('script[src*="judgeme"][src*="loader.js"]');
    if (!tag) { JDGM.gated = false; api.state.judgeme = 'brak-app-embedu'; return; }
    JDGM.loaderSrc = tag.src;

    /* Deep-linki Judge.me (mail „napisz opinię", link do konkretnej opinii) —
       loader normalnie obsługuje je sam; skoro go wstrzymaliśmy, musimy
       rozpoznać je tutaj i wystartować natychmiast. */
    var h = location.hash, q = location.search;
    if (h === '#judgeme' || h === '#judgeme_product_reviews' ||
        /judgeme_(token|review_uuid|dynamic_form|follow_up_token|upload_pictures|pre_verified)/.test(q)) {
      judgemeStart();
      return;
    }

    var nodes = doc.querySelectorAll(JDGM_SEL);
    if (!nodes.length) { api.state.judgeme = 'pominiete-brak-widgetu'; return; }

    /* Strażnik powtórnych wejść: przy `shopify:section:load` / `pageshow`
       obserwator jest już uzbrojony — nie mnożymy IntersectionObserverów. */
    if (JDGM.armed) return;
    JDGM.armed = true;

    if (!('IntersectionObserver' in window)) { judgemeStart(); return; }

    /* rootMargin 800 px — widget zdąży się zbudować, zanim użytkownik do niego
       dojedzie, a jednocześnie nic nie pobiera się na stronach, na których
       sekcji opinii nie ma (kolekcja, strona główna). */
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); judgemeStart(); return; }
      }
    }, { rootMargin: '800px 0px' });
    for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);
    api.state.judgeme = 'uzbrojone-IO-800px';
  }

  /* ==========================================================================
   * C. SHOPIFY FORMS — ładowanie WARUNKOWE, nie odroczone
   * ========================================================================== */

  var FORMS = { armed: false, mo: null, rozbrojony: false };

  function rozbrojFormsMo() {
    if (!FORMS.mo) return;
    try { FORMS.mo.disconnect(); } catch (e) { /* noop */ }
    FORMS.mo = null;
    FORMS.rozbrojony = true;
    api.state.formsMo = 'rozbrojony@' + now() + 'ms';
  }

  function wireForms() {
    if (GATES.forms.open || GATES.forms.rozstrzygnieta) return;

    function armObservers(nodes) {
      if (FORMS.armed) return;
      FORMS.armed = true;
      rozbrojFormsMo();

      if (!('IntersectionObserver' in window)) { openGate('forms'); return; }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { io.disconnect(); openGate('forms'); return; }
        }
      }, { rootMargin: '600px 0px' });
      for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);

      /* Działanie użytkownika prowadzące do formularza — klik/fokus/klawiatura
         wewnątrz <shopify-form>. Zabezpiecza przypadek, w którym IO nie zdąży
         (np. skok kotwicą prosto na formularz). */
      ['pointerdown', 'focusin', 'keydown'].forEach(function (ev) {
        doc.addEventListener(ev, function handler(e) {
          var t = e.target;
          if (t && t.closest && t.closest('shopify-form')) {
            doc.removeEventListener(ev, handler, true);
            openGate('forms');
          }
        }, true);
      });
      api.state.forms = 'uzbrojone-IO-600px (' + nodes.length + ' formularzy)';
    }

    var found = doc.querySelectorAll('shopify-form');
    if (found.length) { armObservers(found); return; }

    /* Brak formularza w DOM → nie pobieramy 206 KiB. Zostaje tylko czujka na
       formularze wstrzykiwane później (Section Rendering API, szybki podgląd,
       layout „popup" wstawiany przez shopify-forms-loader.js).
       R6W6: czujka ma wstępny filtr (interesują nas wyłącznie DODANE ELEMENTY)
       i rozbraja się 12 s po `load` — wcześniej żyła do końca sesji i przy
       każdej mutacji przeszukiwała cały dokument. */
    api.state.forms = 'pominiete-brak-shopify-form';
    if (FORMS.mo || FORMS.rozbrojony) return;
    try {
      FORMS.mo = new MutationObserver(function (recs) {
        var jest = false;
        for (var i = 0; i < recs.length && !jest; i++) {
          var add = recs[i].addedNodes;
          for (var j = 0; j < add.length; j++) {
            if (add[j].nodeType === 1) { jest = true; break; }
          }
        }
        if (!jest) return;
        var late = doc.querySelectorAll('shopify-form');
        if (late.length) { rozbrojFormsMo(); FORMS.rozbrojony = false; armObservers(late); }
      });
      FORMS.mo.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
      /* Po `load` + 12 s żaden formularz „popup" już się nie pojawi bez akcji
         użytkownika, a tę i tak łapią nasłuchy pointerdown/focusin z armObservers
         zakładane przy `rescan()`. */
      window.addEventListener('load', function () {
        setTimeout(function () {
          rozbrojFormsMo();
          /* Na tej stronie nie ma i nie bedzie <shopify-form>. Bramka jest
             rozstrzygnieta, wiec podmienione appendChild moze zniknac. */
          if (!FORMS.armed) resolveGate('forms');
        }, 12000);
      }, { once: true });
    } catch (e) { /* noop */ }
  }

  /* ==========================================================================
   * D. ZGODA + KLAVIYO + BANNER COOKIES
   * --------------------------------------------------------------------------
   * Reguła: ŻADNA funkcja marketingowa nie startuje przed zgodą.
   * Atrybucja i popupy Klaviyo czekają na (zgoda) AND (idle po load LUB
   * pierwsza interakcja użytkownika) — kliknięcie „Akceptuję" w bannerze samo
   * w sobie jest interakcją, więc w praktyce atrybucja rusza natychmiast po
   * wyrażeniu zgody.
   * ========================================================================== */

  var consentOK = false, engaged = false, zgodaRozstrzygnieta = false;

  /* R7-A3 — JEDYNY PUNKT WSTAWIENIA BOOTSTRAPU KLAVIYO.
     `snippets/christina-klaviyo-onsite.liquid` nie emituje juz <script src>
     dla static.klaviyo.com — publikuje tylko adres w window.__ccKlaviyoSrc.
     Skrypt wstawiamy stad, dopiero po zgodzie. Przed zgoda do domen Klaviyo
     (static.klaviyo.com / a.klaviyo.com / fast.a.klaviyo.com) nie idzie ani
     jedno zadanie. Zdarzenia zebrane wczesniej czekaja w window._learnq
     i klaviyo.js odtwarza je przy starcie — atrybucja bez ubytku. */
  function startKlaviyo() {
    if (window.__ccKlaviyoStarted) return;
    window.__ccKlaviyoStarted = true;
    var src = window.__ccKlaviyoSrc;
    if (!src) return;
    try {
      var s = doc.createElement('script');
      s.async = true;
      s.src = src;
      doc.head.appendChild(s);
      mark('klaviyoBootstrap', 'wstawiony');
    } catch (e) { /* noop */ }
  }

  function maybeKlaviyo() {
    if (!consentOK || !engaged) return;
    openGate('klaviyo');   /* najpierw bramka, zeby nie zlapala wlasnej wstawki */
    startKlaviyo();
  }

  function markEngaged() { engaged = true; maybeKlaviyo(); }

  ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'].forEach(function (ev) {
    window.addEventListener(ev, markEngaged, { once: true, passive: true, capture: true });
  });
  afterLoadIdle(markEngaged);

  function pushConsentUpdate(c) {
    if (!c) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'consent_update',
      analytics: !!(c.analyticsAllowed || c.analytics),
      marketing: !!(c.marketingAllowed || c.marketing),
      preferences: !!(c.preferencesAllowed || c.preferences)
    });
  }

  /* R7-A3 — CO ZNACZY „ZGODA".
     Pomiar na produkcji: sam `userCanBeTracked()` zwracal `true`, a
     `currentVisitorConsent()` raportowal marketing/analytics = "yes"
     ZANIM odwiedzajacy cokolwiek kliknal — bo `getShopPrefs().limit` tego
     sklepu jest puste, wiec Shopify stosuje domyslne, przyzwalajace
     ustawienia. Na tej podstawie Klaviyo startowalo w 1,6 s od wejscia.
     Region jest przy tym regulowany: getRegulation() === 'GDPR',
     isRegulationEnforced() === true.
     Dlatego w regionie objetym regulacja wymagamy WYRAZNEJ decyzji:
     `getTrackingConsent()` musi przestac zwracac 'no_interaction'.
     Poza takim regionem zachowanie zostaje bez zmian. */
  /* CZY ODWIEDZAJACY FAKTYCZNIE ZDECYDOWAL.
     Pomiar na produkcji (04.08.2026, PDP mobile, trzy swieze profile):
       bez decyzji : getTrackingConsent() = "no_interaction", marketingAllowed() = false
       „Akceptuj"  : getTrackingConsent() = "yes",            marketingAllowed() = true
       „Odrzuc"    : getTrackingConsent() = "no",             marketingAllowed() = false
     To jest wiarygodny sygnal decyzji i na nim sie opieramy.
     UWAGA na dwie pulapki, obie sprawdzone doswiadczalnie:
       · ciasteczko _tracking_consent NIE POWSTAJE na tym sklepie — Shopify
         wiaze wybor z _shopify_essential. Wersja r7a3-1.7 wymagala tego
         ciasteczka i przez to po „Akceptuj" Klaviyo sie nie ladowalo.
         Zostaje wylacznie jako sygnal dodatkowy (logiczne LUB);
       · `userCanBeTracked()` zwraca true JESZCZE PRZED decyzja, bo
         `getShopPrefs().limit` tego sklepu jest puste. Dlatego samo
         userCanBeTracked() nie moze byc podstawa do startu Klaviyo. */
  function decyzjaZapisana(cp) {
    try {
      if (cp && typeof cp.getTrackingConsent === 'function') {
        var v = cp.getTrackingConsent();
        if (v === 'no_interaction') return false;
        if (v) return true;
      }
    } catch (e) { /* noop */ }
    try {
      if (doc.cookie && doc.cookie.indexOf('_tracking_consent=') > -1) return true;
    } catch (e) { /* noop */ }
    return false;
  }

  function regionRegulowany(cp) {
    try {
      if (cp && typeof cp.isRegulationEnforced === 'function') return !!cp.isRegulationEnforced();
    } catch (e) { /* noop */ }
    return false;
  }

  function consentGranted() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false;
    if (regionRegulowany(cp) && !decyzjaZapisana(cp)) return false;
    try {
      if (typeof cp.marketingAllowed === 'function' && cp.marketingAllowed()) return true;
      if (typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed()) return true;
      if (typeof cp.userCanBeTracked === 'function' && cp.userCanBeTracked()) return true;
    } catch (e) { /* noop */ }
    return false;
  }

  function evaluateConsent() {
    if (consentOK) return;
    if (consentGranted()) {
      consentOK = true;
      zgodaRozstrzygnieta = true;
      mark('zgoda', 'udzielona');
      maybeKlaviyo();
    }
  }

  /* Decyzja zapadla (zgoda ALBO odmowa). Przy odmowie nic juz nie wstawimy,
     wiec bramka Klaviyo jest rozstrzygnieta i podmienione appendChild moze
     wrocic do oryginalu. Odmowa NIE moze uruchomic Klaviyo pozniej — ani
     przez idle, ani przez interakcje: startKlaviyo wola wylacznie
     maybeKlaviyo(), a ten wymaga consentOK. */
  function rozstrzygnijZgode() {
    evaluateConsent();
    if (zgodaRozstrzygnieta) { ukryjSkipLink(); return; }
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!decyzjaZapisana(cp)) return;
    zgodaRozstrzygnieta = true;
    mark('zgoda', 'odrzucona');
    resolveGate('klaviyo');
    ukryjSkipLink();
  }

  function poZdarzeniuZgody() {
    rozstrzygnijZgode();
    setTimeout(rozstrzygnijZgode, 300);
    setTimeout(rozstrzygnijZgode, 1200);
  }

  var privacyReady = false;
  function onPrivacyReady() {
    if (privacyReady) return;
    privacyReady = true;
    var cp = window.Shopify.customerPrivacy;
    try {
      cp.onVisitorConsentCollected(function (c) { pushConsentUpdate(c); poZdarzeniuZgody(); });
    } catch (e) { /* noop */ }
    try {
      var cur = cp.currentVisitorConsent();
      if (cur && (cur.analyticsAllowed !== undefined || cur.analytics !== undefined)) pushConsentUpdate(cur);
      if (decyzjaZapisana(cp)) rozstrzygnijZgode();
    } catch (e) { /* noop */ }
    mark('privacyApi', 'gotowe');
    evaluateConsent();
    startCookieBanner();
  }

  doc.addEventListener('shopify:customer-privacy:api-loaded', function () {
    if (window.Shopify && window.Shopify.customerPrivacy) onPrivacyReady();
  }, { once: true });
  doc.addEventListener('visitorConsentCollected', poZdarzeniuZgody);
  doc.addEventListener('trackingConsentAccepted', poZdarzeniuZgody);

  /* Shopify.loadFeatures pojawia się dopiero z {{ content_for_header }},
     czyli PO tym pliku — stąd krótkie odpytywanie zamiast pojedynczego
     wywołania. Ładujemy API zgody możliwie wcześnie, żeby banner nie czekał
     na round-trip dopiero po starcie cookie-banner.js (to była jedna ze
     składowych 11 s).

     R6W6: odstęp ROŚNIE (0 → 800 ms, maks. 20 prób ≈ 8 s) zamiast stałych
     40 ms przez 10 s. Poprzednia wersja tworzyła do 250 zadań; Lighthouse
     przypisywał tej lawinie zadania, w których faktyczną pracę wykonywały
     skrypty aplikacji. Pomiar produkcyjny po zmianie: `state.privacyPoll`
     pokazuje `prob:1`, czyli API zgody ruszamy przy DRUGIM podejściu —
     pozostałe 248 timerów starej wersji było czystą stratą.
     Pierwsza ponowna próba ma odstęp 0 ms (a nie 40 ms), bo
     `{{ content_for_header }}` parsuje się bezpośrednio za tym plikiem:
     wystarczy oddać sterowanie na jedno zadanie, żeby `Shopify.loadFeatures`
     już istniało. Każde 40 ms opóźnienia tutaj przekłada się 1:1 na moment
     pokazania bannera zgody. */
  (function pollPrivacy(proba, odstep) {
    proba = proba || 0;
    odstep = odstep === undefined ? 0 : odstep;
    if (window.Shopify && window.Shopify.customerPrivacy) { onPrivacyReady(); return; }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      api.state.privacyPoll = 'prob:' + proba;
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
          if (window.Shopify.customerPrivacy) onPrivacyReady();
        });
      } catch (e) { /* noop */ }
      return;
    }
    if (proba < 20) {
      setTimeout(function () {
        pollPrivacy(proba + 1, Math.min(odstep ? odstep * 2 : 20, 800));
      }, odstep);
    }
  })();

  /* --- Banner cookies -------------------------------------------------------
   * Zastane 11 s przy ustawieniu 5 s = suma czterech opóźnień:
   *   1) cookie-banner.js jest `defer`, więc klasa <cookie-banner> powstaje
   *      dopiero po sparsowaniu całego dokumentu;
   *   2) w konstruktorze, gdy theme.config.isTouch (czyli na KAŻDYM urządzeniu
   *      dotykowym), init() nie jest wołany od razu, tylko oddawany do
   *      theme.initWhenVisible + theme.utils.throttle — banner czeka na
   *      widoczność elementu i na okno throttle;
   *   3) dopiero wtedy startuje Shopify.loadFeatures('consent-tracking-api'),
   *      czyli kolejny round-trip sieciowy;
   *   4) i dopiero po nim rusza setTimeout(show, delay * 1000).
   * Kroki 1–3 to było ok. 6 s narzutu ponad ustawione 5 s.
   *
   * Naprawa: API zgody ładujemy równolegle od razu (wyżej), a element dostaje
   * data-delay="0" jeszcze w trakcie parsowania i init() natychmiast po
   * zdefiniowaniu klasy — z pominięciem drogi przez initWhenVisible.
   * Banner pokazuje się więc od razu po ustaleniu, że zgoda jest wymagana.
   * W sections/overlay-group.json wartość „delay" zostaje na minimum schematu
   * (2 s) — to wartość informacyjna dla panelu; kontroler wymusza 0.
   */
  (function forceZeroDelay() {
    if (DESIGN) return;
    try {
      var mo = new MutationObserver(function () {
        var el = doc.getElementById('CookieBanner') || doc.querySelector('cookie-banner');
        if (el) { mo.disconnect(); el.setAttribute('data-delay', '0'); }
      });
      mo.observe(doc.documentElement, { childList: true, subtree: true });
      doc.addEventListener('DOMContentLoaded', function () { try { mo.disconnect(); } catch (e) {} });
    } catch (e) { /* noop */ }
  })();

  /* --- Dostępność bannera zgody — SPÓJNIE NIEMODALNY (R7-A3) ------------
   * Stan zastany po rundzie 6 byl POLOWICZNY: banner mial ustawiane
   * aria-modal="true" i pulapke na Tab (fokus klawiatury nie mogl z niego
   * wyjsc), ale mysza dalo sie normalnie klikac tresc pod nim. Dwa rozne
   * kontrakty dla dwoch urzadzen wejsciowych to gorszy stan niz ktorykolwiek
   * z nich osobno: uzytkownik klawiatury byl uwieziony, uzytkownik myszy nie.
   *
   * Ujednolicamy na NIEMODALNY — zgodnie z projektem (dolny arkusz bez
   * zaslony) i z rola bannera: wybor zgody nie moze byc warunkiem korzystania
   * ze strony.
   *   · aria-modal zostaje "false", pulapka fokusu USUNIETA, strona nie jest
   *     blokowana ani dla klawiatury, ani dla myszy;
   *   · zeby banner nie byl 11. przystankiem Tab, wstawiamy na SAM POCZATEK
   *     <body> odsylacz pomijany wzrokowo („skip link"), ktory pojawia sie
   *     przy pierwszym Tab i przenosi fokus wprost na „Akceptuj";
   *   · odsylacz istnieje wylacznie wtedy, gdy banner jest pokazany, i znika
   *     po dokonaniu wyboru.
   *
   * WYDAJNOSC — TO BYLA NAJDROZSZA FUNKCJA CHRISTINY. Slad CDP (r7, PDP
   * mobile / kolekcja mobile) przypisal starej funkcji `widoczny`
   * 126,0 ms i 272,0 ms czasu wlasnego. Caly ten koszt to WYMUSZONE
   * PRZELICZENIE UKLADU: `getBoundingClientRect()` + `getComputedStyle()`
   * wolane z MutationObservera i z czterech timerow kontrolnych. Przy
   * dlawieniu 4x pojedynczy taki odczyt na duzej stronie kosztuje
   * kilkadziesiat milisekund, wiec kilka wywolan robi kilkaset.
   * Nowa wersja NIE CZYTA UKLADU ANI RAZU: o pokazaniu bannera mowi
   * obecnosc albo brak atrybutu `hidden`, czyli czysty odczyt atrybutu.
   *
   * Blokada skryptow niekoniecznych jest od tego niezalezna — pilnuja jej
   * bramki z sekcji A, wiec przewiniecie strony w dol z pominieciem bannera
   * niczego nie uruchamia.
   */
  var SKIP_ID = 'CcSkipToCookie';
  var SKIP_TXT = {
    pl: 'Przejdź do ustawień plików cookie',
    en: 'Skip to cookie settings',
    zh: '前往 Cookie 設定'
  };

  function jezykStrony() {
    var l = ((doc.documentElement && doc.documentElement.getAttribute('lang')) || 'pl').toLowerCase();
    if (l.indexOf('zh') === 0) return 'zh';
    if (l.indexOf('en') === 0) return 'en';
    return 'pl';
  }

  function stylSkipLinku() {
    if (doc.getElementById('CcSkipStyle')) return;
    var s = doc.createElement('style');
    s.id = 'CcSkipStyle';
    /* Brand Book: Raleway, tekst #000, akcent i obrys #535458, tlo #fff.
       Cel dotykowy 44x44. Fokus 2 px + odsuniecie 3 px (zasada T58 nr 5). */
    s.textContent =
      '#' + SKIP_ID + '{position:fixed;top:8px;left:8px;z-index:2147483000;' +
      'transform:translateY(-200%);opacity:0;pointer-events:none;' +
      'min-height:44px;min-width:44px;padding:12px 20px;' +
      'border:1px solid #535458;border-radius:2px;background:#fff;color:#000;' +
      'font-family:Raleway,"Helvetica Neue",Arial,sans-serif;font-size:.8125rem;' +
      'font-weight:600;letter-spacing:.04em;line-height:1.2;cursor:pointer;' +
      'transition:transform .15s ease,opacity .15s ease}' +
      '#' + SKIP_ID + ':focus{transform:translateY(0);opacity:1;pointer-events:auto;' +
      'outline:2px solid #535458;outline-offset:3px}';
    (doc.head || doc.documentElement).appendChild(s);
  }

  function pierwszaKontrolka(el) {
    return el.querySelector('button[name="accept"]') ||
           el.querySelector('button:not([disabled]),a[href]');
  }

  function pokazSkipLink(el) {
    if (DESIGN || doc.getElementById(SKIP_ID) || !doc.body) return;
    stylSkipLinku();
    var b = doc.createElement('button');
    b.type = 'button';
    b.id = SKIP_ID;
    b.textContent = SKIP_TXT[jezykStrony()];
    b.addEventListener('click', function () {
      var cel = pierwszaKontrolka(el);
      if (!cel) return;
      try { cel.focus({ preventScroll: true }); } catch (e) { try { cel.focus(); } catch (e2) {} }
    });
    if (doc.body.firstChild) doc.body.insertBefore(b, doc.body.firstChild);
    else doc.body.appendChild(b);
    mark('bannerSkipLink', 'wstawiony');
  }

  function ukryjSkipLink() {
    var b = doc.getElementById(SKIP_ID);
    if (!b || !b.parentNode) return;
    b.parentNode.removeChild(b);
    mark('bannerSkipLink', 'zdjety');
  }

  function wireCookieBannerA11y(el) {
    if (DESIGN || !el || el.__ccA11y) return;
    el.__ccA11y = true;

    /* Utrwalamy niemodalnosc — na wypadek gdyby motyw albo aplikacja
       probowaly podniesc banner do rangi modala. */
    el.setAttribute('aria-modal', 'false');
    el.removeAttribute('aria-hidden');

    /* Pokazany = brak atrybutu `hidden`. Czysty odczyt atrybutu; poprzednia
       wersja liczyla w tym miejscu getBoundingClientRect i getComputedStyle. */
    function sprawdz() {
      if (el.hasAttribute('hidden')) ukryjSkipLink();
      else pokazSkipLink(el);
    }

    try {
      new MutationObserver(sprawdz).observe(el, {
        attributes: true, attributeFilter: ['hidden', 'class']
      });
    } catch (e) { /* noop */ }

    doc.addEventListener('visitorConsentCollected', ukryjSkipLink);
    doc.addEventListener('trackingConsentAccepted', ukryjSkipLink);
    el.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('button, a')) setTimeout(sprawdz, 350);
    }, true);

    sprawdz();
  }

  function startCookieBanner() {
    if (DESIGN || !window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('cookie-banner').then(function () {
      var el = doc.getElementById('CookieBanner') || doc.querySelector('cookie-banner');
      if (!el) return;
      el.setAttribute('data-delay', '0');
      try { wireCookieBannerA11y(el); } catch (e) { /* noop */ }
      if (!el.initialized && typeof el.init === 'function') {
        try { el.init(); mark('cookieBanner', 'init'); } catch (e) { /* noop */ }
      }

      /* R7-A3 — DLACZEGO BANNER TRZEBA WYMUSIC.
         `assets/cookie-banner.js` pokazuje banner tylko wtedy, gdy
             !Shopify.customerPrivacy.userCanBeTracked()
             && getTrackingConsent() === 'no_interaction'
         Na tym sklepie `getShopPrefs().limit` jest PUSTE (Ustawienia →
         Prywatność klienta nie maja wlaczonych ograniczen przetwarzania),
         wiec `userCanBeTracked()` zwraca `true` JESZCZE PRZED jakakolwiek
         decyzja. Pomiar na produkcji: banner nie pokazywal sie NIGDY,
         a `shouldShowGDPRBanner()` zwracalo false — mimo ze
         `getRegulation()` to 'GDPR', a `isRegulationEnforced()` to true.
         Skoro Klaviyo jest teraz blokowane do czasu WYRAZNEJ decyzji
         (patrz consentGranted), banner musi miec szanse te decyzje zebrac —
         inaczej nikt nigdy by jej nie podjal i atrybucja padlaby na zawsze.
         Pokazujemy go wiec sami, gdy region jest regulowany, a decyzji brak.
         TRWALE rozwiazanie jest po stronie Shopify Admin → Ustawienia →
         Prywatnosc klienta (ograniczenia przetwarzania dla regionu UE);
         ponizsze jest obejsciem po stronie motywu. */
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof el.load !== 'function' || el.__ccWymuszony) return;
        if (regionRegulowany(cp) && !decyzjaZapisana(cp)) {
          el.__ccWymuszony = true;
          el.load(0);
          mark('cookieBanner', 'wymuszony-brak-decyzji');
        }
      } catch (e) { /* noop */ }
    }).catch(function () { /* noop */ });
  }

  /* ==========================================================================
   * E. WŁASNE SKRYPTY MOTYWU — lista życzeń, AI Advisor
   * ========================================================================== */

  function loadOnce(src, flag) {
    if (!src || window[flag]) return;
    window[flag] = true;
    var e = doc.createElement('script');
    e.src = src;
    e.defer = true;
    doc.head.appendChild(e);
  }

  var wishlistWired = false;
  function wireWishlist() {
    if (!CFG.wishlist || wishlistWired) return;
    wishlistWired = true;
    doc.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-cb-wishlist-toggle]')) {
        loadOnce(CFG.wishlist, '__cbWishlistRequested');
      }
    }, true);
    afterLoadIdle(function () { loadOnce(CFG.wishlist, '__cbWishlistRequested'); });
  }

  var advisorWired = false;
  function wireAdvisor() {
    if (!CFG.advisor || !CFG.advisorEnabled || advisorWired) return;
    advisorWired = true;

    /* T-MOD8 (2026-10-31): czat otwiera ulamek procenta odslon, a jedyne wejscia to [data-cca-open] w naglowku i menu.
       54 KB JS i 25 KB CSS laduja sie przy intencji (najechanie, fokus, dotyk, klik), nie po load na kazdej stronie.
       CSS przed JS, zeby panel nie mignal bez stylu. */
    window.__ccaLoadAdvisor = function () {
      if (window.__ccaAdvisorCssRequested) return;
      window.__ccaAdvisorCssRequested = true;
      var go = function () { loadOnce(CFG.advisor, '__ccaAdvisorRequested'); };
      if (!CFG.advisorCss) { go(); return; }
      var l = doc.createElement('link');
      l.rel = 'stylesheet';
      l.href = CFG.advisorCss;
      l.onload = go;
      l.onerror = go;
      doc.head.appendChild(l);
    };
    var ccaIntent = function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[data-cca-open]')) window.__ccaLoadAdvisor();
    };
    doc.addEventListener('pointerover', ccaIntent, { passive: true, capture: true });
    doc.addEventListener('touchstart', ccaIntent, { passive: true, capture: true });
    doc.addEventListener('focusin', ccaIntent, { passive: true, capture: true });

    function openAdvisor() {
      if (window.ChristinaAdvisor && typeof window.ChristinaAdvisor.open === 'function') {
        window.ChristinaAdvisor.open();
        return true;
      }
      var legacy = doc.querySelector('.cca-launcher');
      if (legacy) { legacy.click(); return true; }
      return false;
    }

    doc.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-cca-open]');
      if (!btn) return;
      e.preventDefault();
      btn.setAttribute('aria-expanded', 'true');
      window.__ccaLoadAdvisor();
      if (!openAdvisor()) {
        var tries = 0;
        var t = setInterval(function () {
          tries++;
          if (openAdvisor()) { clearInterval(t); return; }
          if (tries > 80) {
            clearInterval(t);
            /* T-MOD8: nieudane pobranie nie blokuje kolejnej proby klikniecia. */
            if (!window.ChristinaAdvisor) { window.__ccaAdvisorCssRequested = false; window.__ccaAdvisorRequested = false; }
            btn.setAttribute('aria-expanded', 'false');
          }
        }, 100);
      }
    }, false);

    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = doc.querySelectorAll('[data-cca-open][aria-expanded="true"]');
      for (var i = 0; i < open.length; i++) open[i].setAttribute('aria-expanded', 'false');
    }, false);

    /* Rozgrzewka cache backendu Advisora (P1.5) — w tle, bez blokowania. */
    if (CFG.advisorBackend) {
      afterLoadIdle(function () {
        try {
          var seg = (window.__cca_customer && window.__cca_customer.segment) || 'retail';
          fetch(CFG.advisorBackend + '/api/cache-warmup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ segment: seg }),
            keepalive: true,
            credentials: 'omit'
          }).catch(function () {});
        } catch (e) { /* noop */ }
      });
    }
  }

  /* ==========================================================================
   * F. BRAMKA PROFESJONALNA — wzbogacony komunikat .b2b-only-notice
   * (przeniesione 1:1 z layout/theme.liquid; treści przychodzą z __cbAppsCfg,
   *  żeby zachować lokalizację PL/EN i utrzymać theme.liquid cienkim)
   * ========================================================================== */

  var profWired = false;

  function profRichNotice() {
    var G = CFG.profGate;
    if (!G) return;
    var notices = doc.querySelectorAll('.b2b-only-notice:not(.prof-gate-upgraded)');
    for (var i = 0; i < notices.length; i++) {
      var el = notices[i];
      el.classList.add('prof-gate-upgraded');
      el.innerHTML =
        '<div class="prof-gate-cta" role="region" aria-label="' + G.region + '">' +
        '<p class="prof-gate-cta__title">' + G.title + '</p>' +
        '<p class="prof-gate-cta__desc">' + G.desc + '</p>' +
        '<div class="prof-gate-cta__actions">' +
        '<a href="/account/login" class="prof-gate-cta__btn prof-gate-cta__btn--primary">' + G.cta1 + '</a>' +
        '<a href="/pages/rejestracja-profesjonalna" class="prof-gate-cta__btn prof-gate-cta__btn--secondary">' + G.cta2 + '</a>' +
        '</div>' +
        '<p class="prof-gate-cta__footnote">' + G.foot + '</p>' +
        '</div>';
      var wrap = el.closest('.product-form-wrapper, .product-card, .card-product, [class*="product-form"]');
      if (wrap) wrap.classList.add('prof-locked');
    }
  }

  function wireProfGate() {
    if (!CFG.profGate) return;
    profRichNotice();
    if (profWired) return;
    profWired = true;

    try {
      var scope = doc.getElementById('MainContent') || doc.body;
      if (!scope) return;
      var queued = false;

      /* R6W6: `requestIdleCallback` zamiast `requestAnimationFrame`.
         rAF wykonuje się w zadaniu klatki — razem ze stylem, układem
         i malowaniem — więc doklejał tę pracę do najdroższych zadań
         w całym przebiegu. Bramka Pro nie ma wymagań co do klatki:
         wystarczy, że komunikat jest wzbogacony, zanim użytkownik go
         przeczyta. Limit 500 ms gwarantuje reakcję nawet przy obciążeniu. */
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        idle(function () { queued = false; profRichNotice(); }, 500);
      });
      mo.observe(scope, { childList: true, subtree: true });

      /* Szybki podgląd i inne panele otwierane akcją użytkownika wnoszą
         własne `.b2b-only-notice` po rozbrojeniu obserwatora — stąd
         bezpośrednie odświeżenie na pierwszą interakcję wskaźnikiem. */
      doc.addEventListener('pointerdown', function () { idle(profRichNotice, 400); }, { passive: true, capture: true });

      window.addEventListener('load', function () {
        setTimeout(function () { try { mo.disconnect(); } catch (e) {} }, 6000);
      }, { once: true });
    } catch (e) { /* noop */ }
  }

  /* ==========================================================================
   * F2. FONT MARKI SKLEPU — GTStandard-M -> Raleway
   * --------------------------------------------------------------------------
   * Shopify wstrzykuje w <head> blok
   *     <style data-description="gravity-font-faces">
   * z trzema deklaracjami @font-face dla rodziny 'GTStandard-M'
   * (waga 450 = Regular, 500 = Medium, 600 = Semibold; pliki leżą w
   * shop-assets/static_uploads/shoplift/). Konsumują je komponenty shop-js —
   * <shop-cart-sync> oraz <shopify-account><shop-login> — które trzymają style
   * w adoptedStyleSheets Shadow DOM (`font-family: GTStandard-M, sans-serif`)
   * i NIE mają tam własnego @font-face. Dziedziczą więc deklaracje z dokumentu.
   *
   * DLACZEGO SAM ALIAS NIE WYSTARCZYŁ (pomiar produkcyjny R6W2):
   * blok Shopify NIE jest częścią content_for_header — shop-js dokleja go do
   * <head> DYNAMICZNIE, więc lądował PO naszym aliasie (indeks 423 vs 402
   * w <head>). Przy dwóch pasujących deklaracjach tej samej rodziny algorytm
   * doboru kroju wybiera dopasowanie najbliższe żądanej wadze, a przy remisie
   * — ZADEKLAROWANE PÓŹNIEJ. Nasz zakres 100–900 przegrywał więc z dokładną
   * wagą 500. Efekt: GTStandard-MMedium.woff2 (63 KiB) pobierał się mimo
   * wszystko (start 6653 ms), choć Regular i Semibold już nie.
   *
   * ROZWIĄZANIE: nie ścigamy się o kolejność, tylko PRZEPISUJEMY blok Shopify
   * w miejscu — podmieniamy w nim adresy trzech plików GTStandard na już
   * pobrany plik Raleway. Rodzina i wagi zostają bez zmian, więc komponenty
   * Shop renderują się normalnie, tyle że krojem marki. Żaden plik
   * GTStandard-M*.woff2 (~190 KiB) nie jest pobierany.
   *
   * Blok bywa wstawiony zanim wystartujemy (powrót z cache) albo dopiero po
   * kilku sekundach, dlatego: jednorazowe sprawdzenie + MutationObserver na
   * <head>, rozbrajany po 15 s. R6W6: obserwator reaguje WYŁĄCZNIE na dodanie
   * węzła <style>, więc dziesiątki wstawek <script>/<link> od aplikacji nie
   * uruchamiają już przeszukiwania <head>.
   *
   * UWAGA — ŹRÓDŁO PROBLEMU JEST POZA MOTYWEM. GTStandard-M jest wgrany
   * w Ustawienia → Marka → Typografia. Trwałym rozwiązaniem jest usunięcie go
   * tam (Brand Book Christina dopuszcza wyłącznie Raleway); wtedy Shopify
   * przestanie emitować ten blok na wszystkich powierzchniach (sklep, aplikacja
   * Shop, checkout, maile). Poniższy kod jest obejściem po stronie motywu.
   * ========================================================================== */

  var fontAliased = false;

  function aliasBrandFont() {
    if (fontAliased) return;
    var pre = doc.querySelector('link[rel="preload"][as="font"][href*="Raleway-VariableFont"]');
    var url = pre && pre.getAttribute('href');
    if (!url) return;
    fontAliased = true;

    /* 1. Własna deklaracja zakresowa — obsługuje wagi, których blok Shopify
          nie deklaruje, i działa też wtedy, gdyby bloku nie było wcale. */
    if (!doc.querySelector('style[data-christina="brand-font-alias"]')) {
      var s = doc.createElement('style');
      s.setAttribute('data-christina', 'brand-font-alias');
      s.textContent =
        "@font-face{font-family:'GTStandard-M';" +
        "src:url('" + url + "') format('woff2-variations'),url('" + url + "') format('woff2');" +
        "font-weight:100 900;font-style:normal;font-display:swap;}";
      doc.head.appendChild(s);
    }

    /* 2. Przepisanie bloku Shopify — to jest właściwa dźwignia. */
    var patched = 0;
    function patchGravity() {
      var blocks = doc.querySelectorAll('style[data-description="gravity-font-faces"]:not([data-christina-patched])');
      for (var i = 0; i < blocks.length; i++) {
        var b = blocks[i];
        var txt = b.textContent || '';
        if (txt.indexOf('GTStandard') === -1) continue;
        b.setAttribute('data-christina-patched', '1');
        b.textContent = txt.replace(/url\((['"]?)[^)]*GTStandard-M[^)]*\1\)/g, "url('" + url + "')");
        patched++;
        mark('gravityFontPatch', 'x' + patched);
      }
    }

    patchGravity();
    try {
      var mo = new MutationObserver(function (recs) {
        for (var i = 0; i < recs.length; i++) {
          var add = recs[i].addedNodes;
          for (var j = 0; j < add.length; j++) {
            if (add[j].nodeType === 1 && add[j].tagName === 'STYLE') { patchGravity(); return; }
          }
        }
      });
      mo.observe(doc.head, { childList: true });
      setTimeout(function () { try { mo.disconnect(); } catch (e) {} }, 15000);
    } catch (e) { /* noop */ }

    mark('aliasFontuMarki', 'ok');
  }

  /* ==========================================================================
   * G. START
   * ========================================================================== */

  function boot() {
    try { wireJudgeMe(); } catch (e) { api.state.judgemeError = String(e); }
    try { wireForms(); } catch (e) { api.state.formsError = String(e); }
    try { wireWishlist(); } catch (e) { /* noop */ }
    try { wireAdvisor(); } catch (e) { /* noop */ }
    try { wireProfGate(); } catch (e) { /* noop */ }
    try { aliasBrandFont(); } catch (e) { /* noop */ }
    mark('boot', 'ok');
  }

  /* ==========================================================================
   * H. POWTÓRNE WEJŚCIA — bfcache i doładowanie sekcji
   * --------------------------------------------------------------------------
   * `rescan()` jest w całości idempotentny: każda z funkcji `wire*` ma własnego
   * strażnika (`gate.open`, `JDGM.armed`, `FORMS.armed`, `profWired`,
   * `fontAliased`), więc powtórne wywołanie nie mnoży obserwatorów ani
   * nasłuchów. Wywołujemy go, bo DOM mógł się zmienić między wejściami.
   * ========================================================================== */

  function rescan(zrodlo) {
    try { wireJudgeMe(); } catch (e) { /* noop */ }
    try { wireForms(); } catch (e) { /* noop */ }
    try { wireProfGate(); } catch (e) { /* noop */ }
    try { profRichNotice(); } catch (e) { /* noop */ }
    try { aliasBrandFont(); } catch (e) { /* noop */ }
    mark('rescan', zrodlo);
  }

  window.addEventListener('pageshow', function (e) {
    if (e && e.persisted) rescan('bfcache');
  });
  doc.addEventListener('shopify:section:load', function () { rescan('section-load'); });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
